import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// ===== CONFIG UX OTTIMIZZATA PER HORIZON =====
const UX_CONFIG = {
  // Animazioni fluide ma veloci
  animationDurationMs: 600,
  
  // Messaggi chiari e brevi
  successMessage: '✓ Aggiunto al carrello',
  errorMessages: {
    unavailable: 'Prodotto esaurito',
    quantity: 'Quantità massima raggiunta',
    generic: 'Errore. Riprova.'
  },
  
  // Refresh automatici
  cartRefreshDelayMs: 300,
  
  // Preload immagini
  preloadEnabled: true
};

/**
 * AddToCart - Massima velocità e feedback immediato
 */
class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];
  #animationTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    
    // Preload passivo per velocità
    if (UX_CONFIG.preloadEnabled) {
      this.addEventListener('pointerenter', this.#preloadImage, { 
        passive: true, 
        once: true 
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    // Validazione rapida
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const productForm = this.closest('product-form-component');
    const validation = productForm?.validateQuantity?.();
    if (validation?.error) {
      productForm.showError(validation.message);
      return;
    }

    // Animazione solo se abilitata e non quick-add
    const animate = this.dataset.addToCartAnimation === 'true' && 
                   !event.target.closest('.quick-add-modal');
    
    if (animate) {
      this.#animateFlyToCart();
    }
    
    // Trigger form submit (gestito dal parent)
    this.animateAddToCart();
  }

  #preloadImage = () => {
    preloadImage(this.dataset.productVariantMedia);
  };

  #animateFlyToCart() {
    const button = this.refs.addToCartButton;
    const cartIcon = document.querySelector('.header-actions__cart-icon, [data-cart-icon]');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !button || !image) return;

    const fly = document.createElement('fly-to-cart');
    fly.className = button.classList.contains('quick-add__button') 
      ? 'fly-to-cart--quick' 
      : 'fly-to-cart--main';
    
    fly.style.backgroundImage = `url(${image})`;
    fly.source = button;
    fly.destination = cartIcon;
    
    document.body.appendChild(fly);
  }

  animateAddToCart() {
    const button = this.refs.addToCartButton;
    
    button.dataset.added = 'true';
    
    // Reset rapido dopo animazione
    this.#animationTimeout = setTimeout(() => {
      button.removeAttribute('data-added');
    }, UX_CONFIG.animationDurationMs);
  }
}

/**
 * ProductForm - Esperienza fluida e senza blocchi
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #isSubmitting = false;
  #updateTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    
    // Listener ottimizzati
    const target = this.closest('.shopify-section, dialog, product-card');
    const { signal } = new AbortController();
    
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#updateTimeout) clearTimeout(this.#updateTimeout);
  }

  // Validazione quantità leggera
  validateQuantity() {
    const selector = this.refs.quantitySelector;
    if (!selector?.canAddToCart) return { valid: true };

    const result = selector.canAddToCart();
    return {
      valid: result.canAdd,
      error: !result.canAdd,
      message: result.message || UX_CONFIG.errorMessages.quantity
    };
  }

  // Submit ULTRA VELOCE
  async handleSubmit(event) {
    event.preventDefault();
    
    if (this.#isSubmitting) return;
    
    const variantId = this.refs.variantId.value;
    if (!variantId) return;

    // Validazione minima
    const qtyValid = this.validateQuantity();
    if (!qtyValid.valid) {
      this.showError(qtyValid.message);
      return;
    }

    this.#isSubmitting = true;
    
    // Solo disable temporaneo
    const buttons = this.querySelectorAll('add-to-cart-component');
    buttons.forEach(btn => btn.disable());

    try {
      await this.#addToCart(variantId);
    } catch (error) {
      console.error('Add to cart failed:', error);
      this.showError(UX_CONFIG.errorMessages.generic);
    } finally {
      this.#isSubmitting = false;
      // Re-enable IMMEDIATO
      buttons.forEach(btn => btn.enable());
    }
  }

  async #addToCart(variantId) {
    const form = this.querySelector('form');
    if (!form) throw new Error('No form');

    const formData = new FormData(form);
    
    // Solo sezioni necessarie
    const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map(el => el.dataset.sectionId)
      .filter(Boolean);
    
    sections.forEach(id => formData.append('sections', id));

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.message || 'Errore server');
    }

    // SUCCESS - Feedback immediato
    this.showSuccess(UX_CONFIG.successMessage);
    
    // Refresh cart leggero
    if (this.#updateTimeout) clearTimeout(this.#updateTimeout);
    this.#updateTimeout = setTimeout(() => {
      this.#refreshCart();
    }, UX_CONFIG.cartRefreshDelayMs);

    // Eventi per header/minicart
    this.dispatchEvent(new CartAddEvent({}, variantId, {
      success: true,
      quantity: parseInt(formData.get('quantity') || '1'),
      source: 'product-form'
    }));
  }

  showSuccess(message) {
    this.refs.liveRegion.textContent = message;
    setTimeout(() => {
      this.refs.liveRegion.textContent = '';
    }, 2500);
  }

  showError(message) {
    const errorEl = this.refs.addToCartTextError;
    if (errorEl) {
      errorEl.classList.remove('hidden');
      errorEl.textContent = message;
    }
    
    this.refs.liveRegion.textContent = message;
    
    setTimeout(() => {
      if (errorEl) errorEl.classList.add('hidden');
      this.refs.liveRegion.textContent = '';
    }, 4000);
  }

  async #refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update quantity selector se presente
      const selector = this.refs.quantitySelector;
      if (selector?.setCartQuantity) {
        const variantId = this.refs.variantId.value;
        const item = cart.items.find(i => i.variant_id == variantId);
        selector.setCartQuantity(item?.quantity || 0);
      }
    } catch {
      // Silenzioso - non bloccante
    }
  }

  #onCartUpdate = () => {
    this.#refreshCart();
  };

  #onVariantUpdate = (event) => {
    const variant = event.detail.resource;
    
    // Update variant ID
    this.refs.variantId.value = variant?.id || '';
    
    // Button state
    const container = this.refs.addToCartButtonContainer;
    if (container) {
      if (variant?.available !== false) {
        container.enable();
      } else {
        container.disable();
        this.showError(UX_CONFIG.errorMessages.unavailable);
      }
    }
    
    // Preload immagine
    const media = variant?.featured_media?.preview_image?.src;
    if (media && container) {
      container.setAttribute('data-product-variant-media', media + '?width=100');
    }
  };
}

// ===== REGISTRAZIONE COMPONENTI =====
if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}
if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
